/**
 * `EventStore` over IndexedDB.
 *
 * The only web-specific half of the local-first path, which is exactly why it lives here
 * and not in `@sportsos/sync`: React Native replaces this one file with a SQLite version
 * and the outbox, the backoff and the ack rules carry over untouched (ADR-0010).
 *
 * IndexedDB rather than `localStorage` for the reason the whole ticket exists — a
 * transaction that resolves when the data is durable. `localStorage` is synchronous and
 * gives no such guarantee, so "written before the UI confirms" would be unverifiable.
 *
 * No service worker and no app shell (ADR-0010 puts both out of scope): the page may need
 * the network for its *initial load*. What it must never need the network for is a ball
 * already entered.
 */
import type { Event, EventId, MatchId } from "@sportsos/engine";
import type { EventStore, StoredEvent } from "@sportsos/sync";

import { EVENTS, EVENTS_BY_MATCH, asPromise, committed, database } from "./db.ts";
import { ownerForWrite } from "./ownership.ts";

/** What actually goes on disk. Flat, so IndexedDB can index the parts it needs. */
interface Row {
  readonly eventId: string;
  readonly matchId: string;
  readonly syncedAt: string | null;
  /** Absent on rows written before ownership was recorded; read back as null. */
  readonly writtenBy?: string | null;
  readonly event: Event;
}

export interface IndexedDbEventStoreOptions {
  /**
   * The signed-in account, stamped on every event appended without an explicit owner.
   *
   * Supplied at construction rather than passed at every call site, so the scoring screen
   * cannot forget to record who wrote a ball.
   */
  readonly writtenBy?: () => string | null;
}

export class IndexedDbEventStore implements EventStore {
  private readonly defaultOwner: () => string | null;

  constructor(options: IndexedDbEventStoreOptions = {}) {
    this.defaultOwner = options.writtenBy ?? ((): null => null);
  }

  /**
   * Write one event and resolve only once the transaction has committed.
   *
   * An upsert that preserves `syncedAt`, per the `EventStore` contract: the existing row is
   * read inside the *same* transaction, so a concurrent drain marking this event synced
   * cannot slip between the read and the write and lose its ack.
   */
  async append(matchId: MatchId, event: Event, writtenBy?: string | null): Promise<void> {
    const db = await database();
    const tx = db.transaction(EVENTS, "readwrite");
    const store = tx.objectStore(EVENTS);
    const existing = await asPromise<Row | undefined>(store.get(event.eventId));
    store.put({
      eventId: event.eventId,
      matchId,
      syncedAt: existing?.syncedAt ?? null,
      // An existing event keeps its owner as it is; see `ownerForWrite`.
      writtenBy: ownerForWrite(existing, writtenBy, this.defaultOwner()),
      event,
    } satisfies Row);
    await committed(tx);
  }

  /**
   * Every event for a match, in append order.
   *
   * Order comes free from the key: `eventId` is a UUIDv7, whose leading 48 bits are the
   * millisecond it was created, so IndexedDB's key order *is* creation order. Two events in
   * the same millisecond order arbitrarily but stably, which is harmless — `replay()` sorts
   * by `(inningsNo, seq)` regardless, so ledger array order never decides a cricket number.
   */
  async getAll(matchId: MatchId): Promise<readonly StoredEvent[]> {
    const db = await database();
    const tx = db.transaction(EVENTS, "readonly");
    const index = tx.objectStore(EVENTS).index(EVENTS_BY_MATCH);
    const rows = await asPromise<Row[]>(index.getAll(IDBKeyRange.only(matchId)));
    await committed(tx);
    return rows.map((row) => ({
      event: row.event,
      syncedAt: row.syncedAt,
      writtenBy: row.writtenBy ?? null,
    }));
  }

  async markSynced(
    matchId: MatchId,
    eventIds: readonly EventId[],
    syncedAt: string,
  ): Promise<void> {
    if (eventIds.length === 0) return;
    const db = await database();
    const tx = db.transaction(EVENTS, "readwrite");
    const store = tx.objectStore(EVENTS);
    for (const eventId of eventIds) {
      const row = await asPromise<Row | undefined>(store.get(eventId));
      // Ignore an ack for something not on disk rather than inventing a row for it: the
      // event is the record, and an ack is only ever an annotation on one we wrote.
      if (row === undefined) continue;
      store.put({ ...row, matchId, syncedAt } satisfies Row);
    }
    await committed(tx);
  }

  async pendingCount(matchId: MatchId): Promise<number> {
    const all = await this.getAll(matchId);
    return all.filter((record) => record.syncedAt === null).length;
  }

  /**
   * Every match with an event not yet acked.
   *
   * A cursor over the whole store rather than an index, because "not yet acked" is a null and
   * IndexedDB does not index nulls. The store holds one row per ball, so a few thousand at
   * most, and it is read on a 30-second timer only while the app is visible.
   */
  async matchesWithPending(): Promise<readonly MatchId[]> {
    const db = await database();
    const tx = db.transaction(EVENTS, "readonly");
    const rows = await asPromise<Row[]>(tx.objectStore(EVENTS).getAll());
    await committed(tx);
    const ids = new Set<string>();
    for (const row of rows) if (row.syncedAt === null) ids.add(row.matchId);
    return [...ids] as MatchId[];
  }

  async remove(_matchId: MatchId, eventIds: readonly EventId[]): Promise<void> {
    if (eventIds.length === 0) return;
    const db = await database();
    const tx = db.transaction(EVENTS, "readwrite");
    const store = tx.objectStore(EVENTS);
    for (const eventId of eventIds) store.delete(eventId);
    await committed(tx);
  }

  /** Test and field-support hook: wipe one match. Never called by the scoring loop. */
  async clear(matchId: MatchId): Promise<void> {
    const db = await database();
    const tx = db.transaction(EVENTS, "readwrite");
    const store = tx.objectStore(EVENTS);
    const keys = await asPromise<IDBValidKey[]>(
      store.index(EVENTS_BY_MATCH).getAllKeys(IDBKeyRange.only(matchId)),
    );
    for (const key of keys) store.delete(key);
    await committed(tx);
  }
}
